import { useEffect, useMemo, useRef, useState } from "react";import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, CalendarClock, CheckCircle2, Route, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError, formatDate } from "@/lib/api";
import { Loading } from "@/components/common";
import { Action, EmptyState, PageHeader, Panel, PanelHeader, SearchField, StatusBadge } from "./ui";
import { AgeChip, ageLabel, wardLabel, wardScope } from "@/lib/patient";
import { scrollToSection } from "@/lib/scrollTo";

const onlyDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = String(value).slice(0, 10).split("-");
  return d ? `${d}/${m}/${y}` : value;
};

const TrackCard = ({ track, onOpen }) => {
  const deadline = track.deadlines?.first_contact;
  const waiting = track.deadlines?.inss_waiting;
  return (
    <Panel testId={`timeline-track-card-${track.track}`} className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="er-label text-[11.5px] tracking-[0.16em]">{track.track_label}</p>
          <p
            data-testid={`timeline-track-state-${track.track}`}
            className="mt-1.5 text-[17px] font-semibold leading-snug text-ink-800"
          >
            {track.state_code}. {track.state_label}
          </p>
        </div>
        <StatusBadge tone={track.active ? "info" : "success"}>
          {track.active ? "Em andamento" : "Concluído"}
        </StatusBadge>
      </div>

      <div>
        <div className="h-2 overflow-hidden rounded-full bg-sand">
          <div
            className={`h-full rounded-full ${track.active ? "bg-sage-600" : "bg-emerald-600"}`}
            style={{ width: `${track.progress.percent}%` }}
          />
        </div>
        <p className="mt-1.5 text-[12.5px] text-ink-500">
          Etapa {track.progress.step} de {track.progress.total}
          {track.outcome ? ` · ${track.outcome}` : ""}
        </p>
      </div>

      {track.critical ? (
        <div
          data-testid={`timeline-track-critical-${track.track}`}
          className="flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 px-3 py-2.5 text-[13.5px] font-semibold text-red-800"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{track.critical_label}</span>
        </div>
      ) : null}

      <dl className="space-y-1.5 text-[13.5px] text-ink-600">
        {track.assignee_name ? (
          <div className="flex justify-between gap-3">
            <dt>Responsável</dt>
            <dd className="font-medium text-ink-800">{track.assignee_name}</dd>
          </div>
        ) : null}
        {deadline ? (
          <div className="flex justify-between gap-3">
            <dt>Primeiro contato até</dt>
            <dd className="font-medium text-ink-800">{onlyDate(deadline.deadline)}</dd>
          </div>
        ) : null}
        {waiting ? (
          <div className="flex justify-between gap-3">
            <dt>Liberação INSS</dt>
            <dd className="font-medium text-ink-800">{onlyDate(waiting.release_date)}</dd>
          </div>
        ) : null}
        <div className="flex justify-between gap-3">
          <dt>Última atualização</dt>
          <dd>{formatDate(track.updated_at)}</dd>
        </div>
      </dl>

      <Action
        variant="secondary"
        icon={ArrowRight}
        data-testid={`timeline-open-${track.track}`}
        onClick={onOpen}
        className="mt-auto w-full"
      >
        Abrir tema
      </Action>
    </Panel>
  );
};

const OverdueSection = ({ data, staff, navigate, ward, setWard, wardOptions, wardDefault, scope }) => {
  const [filters, setFilters] = useState({ track: "", state: "", assignee: "", patient: "" });
  if (!data) return <Loading label="Carregando prazos…" />;

  const items = data.items.filter(
    (i) =>
      (!filters.track || i.track === filters.track) &&
      (!filters.state || String(i.state_code) === filters.state) &&
      (!filters.assignee || i.assignee_id === filters.assignee) &&
      (!filters.patient || i.patient_name.toLowerCase().includes(filters.patient.trim().toLowerCase()))
  );
  const states = [...new Map(data.items.map((i) => [i.state_code, i])).values()];
  const tracks = [...new Map(data.items.map((i) => [i.track, i])).values()];

  return (
    <Panel testId="timeline-overdue-section" className="space-y-5">
      <PanelHeader
        label="Prazos atrasados"
        title="Estados com prazo vencido"
        hint="Dias corridos a partir da Data de internação, no fuso America/Sao_Paulo."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div
          className={`rounded-xl px-3.5 py-3 ${
            data.totals.patients > 0 ? "border border-red-300 bg-red-50" : "bg-sand/60"
          }`}
        >
          <p
            className={`flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] ${
              data.totals.patients > 0 ? "text-red-800" : "text-ink-600"
            }`}
          >
            {data.totals.patients > 0 ? <AlertTriangle size={14} aria-hidden="true" /> : null} Pacientes atrasados
          </p>
          <p
            data-testid="overdue-total-patients"
            className={`mt-1 text-2xl font-semibold ${data.totals.patients > 0 ? "text-red-900" : "text-ink-800"}`}
          >
            {data.totals.patients}
          </p>
        </div>
        <div
          className={`rounded-xl px-3.5 py-3 ${
            data.totals.states > 0 ? "border border-red-300 bg-red-50" : "bg-sand/60"
          }`}
        >
          <p
            className={`flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] ${
              data.totals.states > 0 ? "text-red-800" : "text-ink-600"
            }`}
          >
            {data.totals.states > 0 ? <AlertTriangle size={14} aria-hidden="true" /> : null} Estados atrasados
          </p>
          <p
            data-testid="overdue-total-states"
            className={`mt-1 text-2xl font-semibold ${data.totals.states > 0 ? "text-red-900" : "text-ink-800"}`}
          >
            {data.totals.states}
          </p>
        </div>
        {data.totals.by_track.map((t) => (
          <div key={t.track} className="rounded-xl bg-sand/60 px-3.5 py-3">
            <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-600">{t.label}</p>
            <p data-testid={`overdue-total-${t.track}`} className="mt-1 text-2xl font-semibold text-ink-800">
              {t.count}
            </p>
          </div>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        {scope?.locked ? (
          <span
            data-testid="overdue-ward-scope"
            className="inline-flex h-10 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700"
          >
            {scope.label}
          </span>
        ) : (
        <select
          data-testid="overdue-filter-ward"
          aria-label="Ala"
          className={filterInput}
          value={ward}
          onChange={(e) => setWard(e.target.value)}
        >
          {(wardOptions.length ? wardOptions : [{ value: "all", label: "Todas as alas" }]).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        )}
        <select
          data-testid="overdue-filter-track"
          className={filterInput}
          value={filters.track}
          onChange={(e) => setFilters({ ...filters, track: e.target.value })}
        >
          <option value="">Todos os temas</option>
          {tracks.map((t) => (
            <option key={t.track} value={t.track}>
              {t.track_label}
            </option>
          ))}
        </select>
        <select
          data-testid="overdue-filter-state"
          className={filterInput}
          value={filters.state}
          onChange={(e) => setFilters({ ...filters, state: e.target.value })}
        >
          <option value="">Todos os estados</option>
          {states.map((s) => (
            <option key={s.state_code} value={String(s.state_code)}>
              {s.state_code}. {s.state_label}
            </option>
          ))}
        </select>
        <select
          data-testid="overdue-filter-assignee"
          className={filterInput}
          value={filters.assignee}
          onChange={(e) => setFilters({ ...filters, assignee: e.target.value })}
        >
          <option value="">Todos os responsáveis</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <input
          data-testid="overdue-filter-patient"
          className={filterInput}
          placeholder="Filtrar por paciente"
          value={filters.patient}
          onChange={(e) => setFilters({ ...filters, patient: e.target.value })}
        />
        <Action
          variant="secondary"
          data-testid="overdue-filters-clear"
          onClick={() => {
            setFilters({ track: "", state: "", assignee: "", patient: "" });
            setWard(wardDefault);
          }}
        >
          Limpar filtros
        </Action>
      </div>

      {items.length === 0 ? (
        <p data-testid="overdue-empty" className="text-[14px] text-ink-500">
          Nenhum estado com prazo vencido nos filtros atuais.
        </p>
      ) : (
        <ul data-testid="overdue-list" className="space-y-3">
          {items.slice(0, 50).map((i) => (
            <li
              key={i.track_id}
              data-testid={`overdue-item-${i.track_id}`}
              className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-red-300 bg-red-50/70 px-4 py-3.5"
            >
              <div className="min-w-[240px] flex-1">
                <p className="flex items-center gap-2 text-[15px] font-semibold text-ink-900">
                  <AlertTriangle size={16} className="shrink-0 text-red-700" aria-hidden="true" />
                  {i.patient_name} · {i.track_label}
                </p>
                <p className="mt-1 text-[13.5px] text-ink-700">
                  {ageLabel(i)} · Ala: {wardLabel(i)}
                </p>
                <p className="mt-1 text-[13.5px] text-ink-700">
                  Estado {i.state_code}. {i.state_label} · Internação {onlyDate(i.admission_date)} · Limite{" "}
                  {onlyDate(i.deadline)} (internação + {i.deadline_days} dias)
                </p>
                <p className="mt-1 text-[13.5px] font-semibold text-red-800">
                  {i.critical_label}
                  {i.assignee_name ? ` · Responsável: ${i.assignee_name}` : " · Sem responsável definido"}
                  {i.open_pendencies ? ` · ${i.open_pendencies} pendência(s) aberta(s)` : ""}
                </p>
              </div>
              <Action
                variant="secondary"
                icon={ArrowRight}
                size="sm"
                data-testid={`overdue-open-${i.track_id}`}
                onClick={() => navigate(`/portal/linha-do-tempo/${i.patient_id}/${i.track}`)}
              >
                Abrir tema
              </Action>
            </li>
          ))}
          {items.length > 50 ? (
            <li data-testid="overdue-list-more" className="text-[13px] text-ink-500">
              Mostrando os 50 casos com maior atraso de {items.length}. Use os filtros para refinar a lista.
            </li>
          ) : null}
        </ul>
      )}

      {data.admission_pending.length > 0 ? (
        <div data-testid="overdue-admission-pending" className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3.5">
          <p className="flex items-center gap-2 text-[14px] font-semibold text-amber-900">
            <AlertTriangle size={16} aria-hidden="true" /> Pendências cadastrais — sem Data de internação (
            {data.admission_pending.length})
          </p>
          <p className="text-[13px] text-amber-900">
            Estes pacientes não entram na contagem de atrasos até a data ser informada.
          </p>
          <ul className="space-y-1.5">
            {data.admission_pending.map((p) => (
              <li key={p.patient_id} className="flex flex-wrap items-center justify-between gap-2 text-[13.5px] text-ink-700">
                <span>
                  {p.patient_name} · {ageLabel(p)} · Ala: {wardLabel(p)}
                </span>
                <Action
                  variant="secondary"
                  size="sm"
                  data-testid={`overdue-fix-admission-${p.patient_id}`}
                  onClick={() => navigate(`/portal/pacientes/${p.patient_id}`)}
                >
                  Preencher data
                </Action>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Panel>
  );
};

const filterInput =
  "h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15";

export default function PortalTimeline() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [patients, setPatients] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(patientId || null);
  const [timeline, setTimeline] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [staff, setStaff] = useState([]);
  const scope = wardScope(user);
  const [ward, setWard] = useState(scope.value);
  const [wardOptions, setWardOptions] = useState([]);
  const patientSectionRef = useRef(null);

  useEffect(() => {
    if (!selected || timeline === null || !patientSectionRef.current) return;
    scrollToSection(patientSectionRef.current);
  }, [selected, timeline]);

  useEffect(() => {
    api
      .get("/portal/timeline/overdue", { params: { ward } })
      .then(({ data }) => setOverdue(data))
      .catch(() => setOverdue({ totals: { patients: 0, states: 0, by_track: [] }, items: [], admission_pending: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward]);

  useEffect(() => {
    api
      .get("/portal/timeline/staff-options")
      .then(({ data }) => setStaff(data))
      .catch(() => setStaff([]));
    api
      .get("/portal/patients/ward-options")
      .then(({ data }) => setWardOptions(data.items || []))
      .catch(() => setWardOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api
      .get("/portal/patients", { params: { ward, status: "active" } })
      .then(({ data }) => setPatients(data))
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail));
        setPatients([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward]);

  useEffect(() => {
    if (patientId && patientId !== selected) setSelected(patientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  useEffect(() => {
    if (!selected) return;
    setTimeline(null);
    api
      .get(`/portal/timeline/patients/${selected}`)
      .then(({ data }) => setTimeline(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (patients || []).filter((p) => (p.full_name || "").toLowerCase().includes(term));
  }, [patients, query]);

  return (
    <div data-testid="portal-timeline-page" className="space-y-7">
      <PageHeader
        kicker="Acompanhamento por temas"
        title="Linha do Tempo"
        subtitle="Cada tema tem estados, registros obrigatórios e histórico permanente. Todas as validações são conferidas no servidor."
      />

      {selected ? null : (
        <OverdueSection
          data={overdue}
          staff={staff}
          navigate={navigate}
          ward={ward}
          setWard={setWard}
          wardDefault={scope.value}
          scope={scope}
          wardOptions={wardOptions}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-[320px,1fr]">
        <Panel testId="timeline-patient-picker" className="space-y-4">
          <PanelHeader label="Pacientes" title="Selecione um paciente" />
          <SearchField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nome"
            testId="timeline-patient-search"
          />
          {scope.locked ? (
            <span
              data-testid="timeline-ward-scope"
              className="inline-flex h-10 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700"
            >
              {scope.label}
            </span>
          ) : (
          <select
            data-testid="timeline-filter-ward"
            aria-label="Ala"
            className={filterInput}
            value={ward}
            onChange={(e) => setWard(e.target.value)}
          >
            {(wardOptions.length ? wardOptions : [{ value: "all", label: "Todas as alas" }]).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          )}
          {patients === null ? (
            <Loading label="Carregando pacientes…" />
          ) : (
            <ul className="max-h-[420px] space-y-1.5 overflow-y-auto">
              {filtered.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    data-testid={`timeline-select-patient-${p.id}`}
                    onClick={() => {
                      setSelected(p.id);
                      navigate(`/portal/linha-do-tempo/${p.id}`);
                    }}
                    className={`flex w-full items-center justify-between gap-2 rounded-xl px-3.5 py-2.5 text-left text-[14.5px] transition-colors ${
                      selected === p.id ? "bg-sage-600 text-white" : "text-ink-700 hover:bg-sand"
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {p.full_name}
                      <span
                        data-testid={`timeline-patient-age-${p.id}`}
                        className={selected === p.id ? "text-white/85" : "text-ink-500"}
                      >
                        {" "}
                        · {ageLabel(p)}
                      </span>
                    </span>
                    {p.admission_pending ? (
                      <AlertTriangle
                        size={15}
                        className={selected === p.id ? "text-white" : "text-amber-600"}
                        aria-label="Data de internação pendente"
                      />
                    ) : null}
                  </button>
                </li>
              ))}
              {filtered.length === 0 ? <li className="px-3 py-2 text-[13.5px] text-ink-500">Nenhum paciente.</li> : null}
            </ul>
          )}
        </Panel>

        <div className="space-y-6" ref={patientSectionRef}>
          {!selected ? (
            <EmptyState
              testId="timeline-empty-state"
              icon={Route}
              title="Escolha um paciente"
              description="Pesquise e selecione um paciente para ver os temas, estados e o histórico consolidado."
            />
          ) : timeline === null ? (
            <Loading label="Carregando linha do tempo…" />
          ) : (
            <>
              <Panel testId="timeline-patient-header" className="space-y-3">
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <p className="er-label text-[11.5px] tracking-[0.16em]">Paciente</p>
                    <h2 data-testid="timeline-patient-name" className="mt-1 text-2xl font-semibold text-ink-900">
                      {timeline.patient.full_name}
                    </h2>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <AgeChip patient={timeline.patient} testId="timeline-patient-age" />
                      <span data-testid="timeline-patient-ward" className="text-[13px] text-ink-500">
                        Ala: {wardLabel(timeline.patient)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="er-label text-[11.5px] tracking-[0.16em]">Data de internação</p>
                    <p data-testid="timeline-admission-date" className="mt-1 text-[17px] font-semibold text-ink-800">
                      {onlyDate(timeline.patient.admission_date)}
                    </p>
                  </div>
                </div>
                {timeline.patient.admission_pending ? (
                  <div
                    data-testid="timeline-admission-pending"
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3 text-[13.5px] font-semibold text-amber-900"
                  >
                    <AlertTriangle size={16} aria-hidden="true" />
                    <span>Pendência: data de internação não informada. Estados que dependem dela ficam bloqueados.</span>
                    <Action
                      variant="secondary"
                      size="sm"
                      data-testid="timeline-fix-admission"
                      onClick={() => navigate(`/portal/pacientes/${timeline.patient.id}`)}
                    >
                      Preencher no paciente
                    </Action>
                  </div>
                ) : null}
              </Panel>

              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {timeline.tracks.map((t) => (
                  <TrackCard
                    key={t.track}
                    track={t}
                    onOpen={() => navigate(`/portal/linha-do-tempo/${timeline.patient.id}/${t.track}`)}
                  />
                ))}
              </div>

              <Panel testId="timeline-history-panel" className="space-y-4">
                <PanelHeader
                  label="Histórico consolidado"
                  title="Todas as alterações, em ordem cronológica"
                  hint="Os registros permanecem mesmo depois de o tema ser concluído."
                />
                {timeline.history.length === 0 ? (
                  <p className="text-[14px] text-ink-500">Nenhum registro ainda.</p>
                ) : (
                  <ol data-testid="timeline-history-list" className="space-y-3">
                    {timeline.history.map((e) => (
                      <li key={e.id} className="flex gap-3 rounded-xl bg-sand/50 px-3.5 py-3">
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-sage-700" aria-hidden="true" />
                        <div className="flex-1">
                          <p className="text-[14px] font-semibold text-ink-800">
                            {e.track_label}
                            {e.type === "transition" ? ` · ${e.from_label} → ${e.to_label}` : ""}
                          </p>
                          {e.note ? <p className="mt-0.5 text-[13.5px] text-ink-600">{e.note}</p> : null}
                          <p className="mt-1 text-[12.5px] text-ink-500">
                            {formatDate(e.created_at)} · {e.actor_name || "—"}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </Panel>
            </>
          )}
        </div>
      </div>

      <p className="flex items-center gap-2 text-[13px] text-ink-500">
        <Users size={14} aria-hidden="true" /> A Rede de Apoio autorizada é gerenciada no cadastro do paciente e na
        página de Vínculos — a Linha do Tempo apenas consulta esses dados.
        <CalendarClock size={14} aria-hidden="true" /> Datas calculadas no fuso America/Sao_Paulo.
      </p>
    </div>
  );
}
